'use client';

import { useState, useEffect } from 'react';

interface Auth0User {
  sub: string;
  email: string;
  name?: string;
  picture?: string;
  email_verified?: boolean;
}

interface Auth0Session {
  user: Auth0User | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom Auth0 session hook to replace next-auth useSession
 * Following coding standards: Edge Runtime compatible, proper error handling
 */
export function useAuth0Session(): Auth0Session {
  const [session, setSession] = useState<Auth0Session>({
    user: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch('/api/auth/me');
        
        if (response.ok) {
          const data = await response.json();
          setSession({
            user: data.user || null,
            loading: false,
            error: null
          });
        } else if (response.status === 401) {
          // Not authenticated
          setSession({
            user: null,
            loading: false,
            error: null
          });
        } else {
          throw new Error('Failed to fetch session');
        }
      } catch (error) {
        setSession({
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Session error'
        });
      }
    };

    fetchSession();
  }, []);

  return session;
}

/**
 * Auth0 session utilities
 */
export const auth0Session = {
  /**
   * Get current session from server-side
   */
  getServerSession: async (): Promise<Auth0User | null> => {
    try {
      // In server-side context, check cookies directly
      const { cookies } = await import('next/headers');
      const cookieStore = cookies();
      const userCookie = cookieStore.get('auth0-user');
      
      if (userCookie) {
        return JSON.parse(userCookie.value);
      }
      
      return null;
    } catch (error) {
      console.error('Error getting server session:', error);
      return null;
    }
  },

  /**
   * Sign out helper
   */
  signOut: () => {
    window.location.href = '/api/auth/logout';
  },

  /**
   * Sign in helper
   */
  signIn: () => {
    window.location.href = '/api/auth/login';
  }
};